import React, { useState, useEffect } from 'react';
import { X, Calendar, PoundSterling, FileText, Clock, RefreshCw, Trash2, MapPin, Navigation, Loader2, Users, Route } from 'lucide-react';
import AddressPickerModal from './AddressPickerModal';
import MiniMapPreview from './MiniMapPreview';
import { openGoogleMapsNavigation } from '../utils/navigation';
import { jobChanged, Actions } from '../utils/dataSync';
import { persistCustomService } from '../utils/persistCustomService';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const JobEditModal = ({ 
  job, 
  onClose, 
  onSave,
  onDelete,
  onReschedule 
}) => {
  const [formData, setFormData] = useState({
    service: '',
    price: 0,
    scheduled_date: '',
    notes: '',
    invoice_notes: '',
    pricing_mode: 'flat',
    line_items: [],
    frequency: '',
    assignee: '',
    round: '',
    address: '',
    latitude: null,
    longitude: null
  });
  const [originalPrice, setOriginalPrice] = useState(0);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const [showAddressPicker, setShowAddressPicker] = useState(false);
  const [showPriceWarning, setShowPriceWarning] = useState(false);
  const [services, setServices] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [rounds, setRounds] = useState([]);
  const [loadingServices, setLoadingServices] = useState(true);
  const [frequencyOptions, setFrequencyOptions] = useState([
    { value: '6', label: '6 Weekly' },
    { value: '12', label: '12 Weekly' }
  ]);
  const [showCustomFrequency, setShowCustomFrequency] = useState(false);
  const [customFrequencyValue, setCustomFrequencyValue] = useState('');
  const [defaultFrequency, setDefaultFrequency] = useState('6');
  // Preserve the original rich frequency string (e.g. "every 6 weeks on Thursday")
  // because the dropdown only carries the weeks-number. If the user doesn't
  // touch the dropdown we send the original string back unchanged on save —
  // otherwise the day-of-week info would be lost. (Kym Wood bug, Jun 2026.)
  const [originalFrequencyString, setOriginalFrequencyString] = useState('');
  const [originalFrequencyParsed, setOriginalFrequencyParsed] = useState('');

  // Fetch services, team members, rounds and frequency settings
  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = localStorage.getItem('streakfree_token');
        
        // Fetch services
        const servicesRes = await fetch(`${API_URL}/api/services`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (servicesRes.ok) {
          const data = await servicesRes.json();
          setServices(data);
        }
        
        // Fetch team members for assignee dropdown
        const teamRes = await fetch(`${API_URL}/api/team/users`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (teamRes.ok) {
          const teamData = await teamRes.json();
          setTeamMembers(teamData.users || []);
        }
        
        // Fetch rounds for round dropdown
        const roundsRes = await fetch(`${API_URL}/api/rounds`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (roundsRes.ok) {
          const roundsData = await roundsRes.json();
          setRounds(roundsData);
        }
        
        // Fetch settings for frequency options
        const settingsRes = await fetch(`${API_URL}/api/settings`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (settingsRes.ok) {
          const settings = await settingsRes.json();
          if (settings.custom_frequencies && Array.isArray(settings.custom_frequencies) && settings.custom_frequencies.length > 0) {
            setFrequencyOptions(settings.custom_frequencies);
          }
          if (settings.default_frequency) {
            setDefaultFrequency(settings.default_frequency);
          }
        }
      } catch (err) {
        console.error('Error fetching data:', err);
      } finally {
        setLoadingServices(false);
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    if (job) {
      // Parse frequency from various formats (CSV imports have "every X weeks on Day" format)
      let freq = job.frequency || '';
      const originalFreq = freq; // Keep for debugging
      
      // Extract number from frequency string like "every 6 weeks on Monday"
      const weekMatch = freq.match(/(\d+)\s*week/i);
      if (weekMatch) {
        freq = weekMatch[1]; // Extract just the number
      } else if (freq === 'every 6 weeks' || freq === '6 weeks' || freq.toLowerCase().includes('6 week')) {
        freq = '6';
      } else if (freq === 'every 12 weeks' || freq === '12 weeks' || freq.toLowerCase().includes('12 week')) {
        freq = '12';
      } else if (freq === 'every 4 weeks' || freq === '4 weeks' || freq.toLowerCase().includes('4 week')) {
        freq = '4';
      } else if (freq === 'every 8 weeks' || freq === '8 weeks' || freq.toLowerCase().includes('8 week')) {
        freq = '8';
      } else if (freq.toLowerCase().includes('ad hoc') || freq.toLowerCase().includes('one off') || freq.toLowerCase().includes('one-off') || job.is_one_off) {
        freq = ''; // One-off job
      } else if (!freq || freq === 'one-time' || freq === 'One-time') {
        freq = '';
      } else if (!isNaN(parseInt(freq))) {
        freq = String(parseInt(freq)); // Keep numeric values as-is
      }
      
      console.log(`[JobEditModal] Frequency parsed: "${originalFreq}" -> "${freq}"`);
      // Stash the rich string so we can preserve it on save unless the user changes the dropdown.
      setOriginalFrequencyString(originalFreq);
      setOriginalFrequencyParsed(freq);
      
      const jobPrice = job.price || 0;
      setOriginalPrice(jobPrice);
      
      // ALWAYS use job's actual frequency - never default for existing jobs
      const effectiveFrequency = job.id ? freq : (freq || defaultFrequency);
      
      // Get round - check multiple possible fields
      const jobRound = (job.round || job.zone || '').trim();
      
      console.log(`[JobEditModal] Loading job:`, {
        id: job.id,
        frequency: effectiveFrequency,
        round: jobRound,
        scheduled_date: job.scheduled_date || job.next_due
      });
      
      setFormData({
        service: job.service || job.services || job.service_type || job.title || '',
        price: jobPrice,
        scheduled_date: (job.scheduled_date || job.date || job.next_due || job.nextDue || '').split('T')[0],
        notes: job.notes || '',
        invoice_notes: job.invoice_notes || '',
        // If the job already has saved line items, surface the breakdown UI
        // by default; otherwise default to flat-price mode (existing behaviour).
        pricing_mode: (Array.isArray(job.line_items) && job.line_items.length > 0) ? 'lines' : 'flat',
        line_items: Array.isArray(job.line_items) ? job.line_items.map(li => ({
          service: li.service || li.description || '',
          rate: Number(li.rate ?? li.unit_price ?? 0),
          quantity: Number(li.quantity ?? 1),
        })) : [],
        frequency: effectiveFrequency,
        assignee: job.assignee || job.assigned_to || '',
        round: jobRound,
        address: job.job_address || job.address_override || job.address || job.customer_address || '',
        latitude: job.latitude || job.lat || null,
        longitude: job.longitude || job.lng || null
      });
    } else {
      // New job - use default frequency
      setFormData(prev => ({ ...prev, frequency: defaultFrequency }));
    }
  }, [job, defaultFrequency]);

  const isCreateMode = !job || !job.id;
  
  // Check if price has changed
  const priceChanged = parseFloat(formData.price) !== parseFloat(originalPrice);
  const isRecurringJob = formData.frequency && formData.frequency !== '';
  
  // Handle save button click - check if we need to show price warning
  const handleSaveClick = () => {
    if (priceChanged && isRecurringJob) {
      setShowPriceWarning(true);
    } else {
      handleSave(false);
    }
  };
  
  // Save with option to update all future jobs
  const handleSave = async (updateAllFuture = false) => {
    setShowPriceWarning(false);
    setIsSaving(true);
    setError('');
    
    try {
      const token = localStorage.getItem('streakfree_token');
      
      if (isCreateMode) {
        setError('Please create jobs from a customer profile or use the schedule.');
        setIsSaving(false);
        return;
      }
      
      // Determine if one-off
      const isOneOff = !formData.frequency || formData.frequency === '';

      // Preserve the rich frequency string ("every 6 weeks on Thursday") when
      // the user hasn't actually changed the dropdown selection. The dropdown
      // only shows the weeks-number so naively sending it back would strip the
      // "on Thursday" info — that's how Kym Wood ended up with frequency="6".
      let frequencyToSend;
      if (isOneOff) {
        frequencyToSend = null;
      } else if (
        originalFrequencyString
        && formData.frequency === originalFrequencyParsed
      ) {
        frequencyToSend = originalFrequencyString;
      } else {
        frequencyToSend = formData.frequency;
      }
      
      // For the scheduled_date the user picks, that IS the next due date
      // The next occurrence AFTER that will be calculated by the backend when the job is completed
      let nextDue = formData.scheduled_date || null;
      
      // When pricing_mode === 'lines', the canonical total is the sum of
      // (rate * quantity) across line items. We still send that sum as
      // `price` so legacy code paths (auto-charge, dashboards, owings) keep
      // working without needing to be rewritten to inspect line_items.
      const lineItemsClean = (formData.line_items || [])
        .map(li => ({
          service: (li.service || '').trim(),
          rate: parseFloat(li.rate) || 0,
          quantity: parseFloat(li.quantity) || 1,
        }))
        .filter(li => li.service);
      const linesTotal = lineItemsClean.reduce(
        (sum, li) => sum + (li.rate * li.quantity), 0
      );
      const usingLines = formData.pricing_mode === 'lines' && lineItemsClean.length > 0;
      const effectivePrice = usingLines ? linesTotal : (parseFloat(formData.price) || 0);
      
      // Edit mode
      const response = await fetch(`${API_URL}/api/jobs/${job.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          service: formData.service,
          price: effectivePrice,
          scheduled_date: formData.scheduled_date,
          notes: formData.notes,
          invoice_notes: formData.invoice_notes,
          pricing_mode: usingLines ? 'lines' : 'flat',
          // Always send the array — empty list clears any previously-saved
          // breakdown if the user toggled back to flat-price mode.
          line_items: usingLines ? lineItemsClean : [],
          frequency: frequencyToSend,
          is_one_off: isOneOff,
          is_recurring: !isOneOff,
          next_due: nextDue,
          assignee: formData.assignee,
          round: formData.round,
          zone: formData.round,
          address: formData.address,
          latitude: formData.latitude,
          longitude: formData.longitude,
          update_all_future: updateAllFuture && priceChanged
        })
      });
      
      if (response.ok) {
        const updatedJob = await response.json();
        // Notify ALL dependent areas via centralized data sync
        jobChanged(Actions.UPDATE, updatedJob);
        if (onSave) onSave(updatedJob);
        onClose();
      } else {
        const data = await response.json();
        setError(data.detail || 'Failed to update job');
      }
    } catch (err) {
      console.error('Error updating job:', err);
      setError('Error updating job. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this job? This action cannot be undone.')) {
      return;
    }
    
    setIsSaving(true);
    try {
      const token = localStorage.getItem('streakfree_token');
      const response = await fetch(`${API_URL}/api/jobs/${job.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        // Notify ALL dependent areas via centralized data sync
        jobChanged(Actions.DELETE, { id: job.id });
        if (onDelete) onDelete(job);
        onClose();
      } else {
        setError('Failed to delete job');
      }
    } catch (err) {
      console.error('Error deleting job:', err);
      setError('Error deleting job');
    } finally {
      setIsSaving(false);
    }
  };

  const handleAddressSave = ({ address, latitude, longitude }) => {
    setFormData(prev => ({
      ...prev,
      address,
      latitude,
      longitude
    }));
  };

  const handleNavigate = () => {
    openGoogleMapsNavigation(formData.latitude, formData.longitude, formData.address);
  };

  if (!job) return null;

  // Handle backdrop click - only close if clicking directly on the backdrop
  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <>
      <div 
        className="fixed inset-0 bg-black/60 z-[120] flex items-center justify-center p-4 safe-area-all" 
        onClick={handleBackdropClick}
        onTouchEnd={(e) => {
          // Only close on touch if touching the backdrop directly
          if (e.target === e.currentTarget) {
            onClose();
          }
        }}
      >
        <div 
          className="bg-white rounded-2xl w-full max-w-md shadow-2xl overflow-hidden modal-shell-h flex flex-col"
          onClick={e => e.stopPropagation()}
          onTouchStart={e => e.stopPropagation()}
          onTouchEnd={e => e.stopPropagation()}
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-emerald-600 to-teal-600 p-4 text-white flex-shrink-0">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold">Edit Job</h3>
              <button 
                onClick={onClose}
                className="p-2 hover:bg-white/10 rounded-xl transition-colors"
              >
                <X size={20} />
              </button>
            </div>
          </div>

          {/* Form */}
          <div className="p-6 space-y-4 overflow-y-auto flex-1">
            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">
                {error}
              </div>
            )}

            {/* Service Type - Multi-select */}
            <div>
              <label className="flex items-center gap-2 text-sm font-medium text-zinc-700 mb-2">
                <FileText size={16} className="text-zinc-400" />
                Services
              </label>
              {loadingServices ? (
                <div className="w-full px-4 py-2.5 rounded-xl border border-zinc-300 flex items-center gap-2 text-zinc-400">
                  <Loader2 size={16} className="animate-spin" />
                  Loading services...
                </div>
              ) : (
                <div className="space-y-2">
                  {/* Selected services tags */}
                  <div className="flex flex-wrap gap-2 min-h-[40px] p-2 rounded-xl border border-zinc-300 bg-zinc-50">
                    {(formData.service || '').split(',').filter(s => s.trim()).map((svc, idx) => (
                      <span 
                        key={idx}
                        className="inline-flex items-center gap-1 px-2 py-1 bg-emerald-100 text-emerald-700 rounded-lg text-sm"
                      >
                        {svc.trim()}
                        <button
                          type="button"
                          onClick={() => {
                            const currentServices = (formData.service || '').split(',').filter(s => s.trim());
                            const newServices = currentServices.filter(s => s.trim() !== svc.trim());
                            setFormData({...formData, service: newServices.join(', ')});
                          }}
                          className="hover:text-emerald-900"
                        >
                          <X size={14} />
                        </button>
                      </span>
                    ))}
                    {!(formData.service || '').trim() && (
                      <span className="text-zinc-400 text-sm">No services selected</span>
                    )}
                  </div>
                  {/* Service dropdown to add */}
                  <select
                    value=""
                    onChange={(e) => {
                      if (e.target.value) {
                        const currentServices = (formData.service || '').split(',').filter(s => s.trim());
                        if (!currentServices.includes(e.target.value)) {
                          const newServices = [...currentServices, e.target.value];
                          setFormData({...formData, service: newServices.join(', ')});
                        }
                      }
                    }}
                    className="w-full px-4 py-2.5 rounded-xl border border-zinc-300 text-zinc-900 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 outline-none bg-white"
                    data-testid="edit-job-service-select"
                  >
                    <option value="">+ Add a service</option>
                    {services.map(service => (
                      <option key={service.id} value={service.name}>
                        {service.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>

            {/* Price — Squeegee-style tabbed picker: flat price OR
                per-service breakdown. Line items show on the invoice when
                more than one service is configured. */}
            <div>
              <label className="flex items-center gap-2 text-sm font-medium text-zinc-700 mb-2">
                <PoundSterling size={16} className="text-zinc-400" />
                Price (£)
              </label>
              <div className="flex bg-zinc-100 rounded-xl p-1 mb-3" role="tablist">
                <button
                  type="button"
                  role="tab"
                  aria-selected={formData.pricing_mode !== 'lines'}
                  onClick={() => setFormData({ ...formData, pricing_mode: 'flat' })}
                  className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    formData.pricing_mode !== 'lines'
                      ? 'bg-white text-zinc-900 shadow-sm'
                      : 'text-zinc-500 hover:text-zinc-700'
                  }`}
                  data-testid="pricing-mode-flat-tab"
                >
                  Price Job
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={formData.pricing_mode === 'lines'}
                  onClick={() => {
                    // First time opening the breakdown? Seed it with the
                    // current flat price as a single line so the user
                    // doesn't have to retype it.
                    const seed = (formData.line_items && formData.line_items.length > 0)
                      ? formData.line_items
                      : [{
                          service: (formData.service || 'Window Clean').split(',')[0].trim(),
                          rate: parseFloat(formData.price) || 0,
                          quantity: 1,
                        }];
                    setFormData({ ...formData, pricing_mode: 'lines', line_items: seed });
                  }}
                  className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    formData.pricing_mode === 'lines'
                      ? 'bg-white text-zinc-900 shadow-sm'
                      : 'text-zinc-500 hover:text-zinc-700'
                  }`}
                  data-testid="pricing-mode-lines-tab"
                >
                  Price Services
                </button>
              </div>
              
              {formData.pricing_mode !== 'lines' ? (
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.price}
                  onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                  className="w-full px-4 py-2.5 rounded-xl border border-zinc-300 text-zinc-900 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 outline-none"
                  data-testid="job-flat-price-input"
                />
              ) : (
                <div className="space-y-3" data-testid="job-line-items-editor">
                  {(formData.line_items || []).map((li, idx) => {
                    const lineTotal = (parseFloat(li.rate) || 0) * (parseFloat(li.quantity) || 1);
                    return (
                      <div key={idx} className="bg-zinc-50 rounded-xl p-3 space-y-2" data-testid={`line-item-${idx}`}>
                        <div className="flex items-start gap-2">
                          {services.length > 0 ? (
                            <select
                              value={li.service}
                              onChange={(e) => {
                                const val = e.target.value;
                                if (val === '__custom__') {
                                  const typed = (window.prompt('New service name:') || '').trim();
                                  if (!typed) return;
                                  persistCustomService(typed, services).then(saved => {
                                    if (saved) setServices(prev => [...prev, saved]);
                                  });
                                  const next = [...formData.line_items];
                                  next[idx] = { ...next[idx], service: typed };
                                  setFormData({ ...formData, line_items: next });
                                  return;
                                }
                                const next = [...formData.line_items];
                                // When the user picks a service from the
                                // dropdown, also seed the rate from the
                                // service's default price (if defined &&
                                // current rate is 0). Keeps the UX fast
                                // for the common case.
                                const svc = services.find(s => (s.name || s.service) === val);
                                next[idx] = {
                                  ...next[idx],
                                  service: val,
                                  rate: (!next[idx].rate && svc?.default_price)
                                    ? Number(svc.default_price) : next[idx].rate,
                                };
                                setFormData({ ...formData, line_items: next });
                              }}
                              className="flex-1 min-w-0 px-3 py-2 rounded-lg border border-zinc-300 text-sm focus:border-emerald-500 outline-none"
                            >
                              <option value="">Choose service…</option>
                              {services.map(s => (
                                <option key={s.id || s.name} value={s.name || s.service}>
                                  {s.name || s.service}
                                </option>
                              ))}
                              {li.service && !services.find(s => (s.name || s.service) === li.service) && (
                                <option value={li.service}>{li.service}</option>
                              )}
                              <option value="__custom__">+ Add new (type your own)…</option>
                            </select>
                          ) : (
                            <input
                              type="text"
                              value={li.service}
                              onChange={(e) => {
                                const next = [...formData.line_items];
                                next[idx] = { ...next[idx], service: e.target.value };
                                setFormData({ ...formData, line_items: next });
                              }}
                              placeholder="Service name"
                              className="flex-1 min-w-0 px-3 py-2 rounded-lg border border-zinc-300 text-sm focus:border-emerald-500 outline-none"
                            />
                          )}
                          <button
                            type="button"
                            onClick={() => {
                              const next = formData.line_items.filter((_, i) => i !== idx);
                              setFormData({ ...formData, line_items: next });
                            }}
                            className="p-2 text-zinc-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                            aria-label="Remove line"
                            data-testid={`line-item-remove-${idx}`}
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                        <div className="grid grid-cols-3 gap-2 items-end">
                          <div>
                            <label className="block text-[11px] text-zinc-500 mb-1">Rate (£)</label>
                            <input
                              type="number"
                              step="0.01"
                              min="0"
                              value={li.rate}
                              onChange={(e) => {
                                const next = [...formData.line_items];
                                next[idx] = { ...next[idx], rate: e.target.value };
                                setFormData({ ...formData, line_items: next });
                              }}
                              className="w-full px-2 py-1.5 rounded-lg border border-zinc-300 text-sm focus:border-emerald-500 outline-none"
                            />
                          </div>
                          <div>
                            <label className="block text-[11px] text-zinc-500 mb-1">Qty</label>
                            <input
                              type="number"
                              step="1"
                              min="1"
                              value={li.quantity}
                              onChange={(e) => {
                                const next = [...formData.line_items];
                                next[idx] = { ...next[idx], quantity: e.target.value };
                                setFormData({ ...formData, line_items: next });
                              }}
                              className="w-full px-2 py-1.5 rounded-lg border border-zinc-300 text-sm focus:border-emerald-500 outline-none"
                            />
                          </div>
                          <div className="text-right">
                            <label className="block text-[11px] text-zinc-500 mb-1">Line Total</label>
                            <div className="font-semibold text-zinc-900 text-sm py-1.5">
                              £{lineTotal.toFixed(2)}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  <button
                    type="button"
                    onClick={() => setFormData({
                      ...formData,
                      line_items: [...(formData.line_items || []), { service: '', rate: 0, quantity: 1 }],
                    })}
                    className="w-full py-2 border-2 border-dashed border-zinc-300 rounded-xl text-sm font-medium text-zinc-600 hover:border-emerald-400 hover:text-emerald-600 transition-colors"
                    data-testid="line-item-add-btn"
                  >
                    + Add service
                  </button>
                  {/* Running total — recomputes as the user edits, mirrors
                      what the saved invoice will show. */}
                  <div className="flex items-center justify-between pt-2 border-t border-zinc-200">
                    <span className="text-sm text-zinc-500">Total</span>
                    <span className="text-lg font-bold text-zinc-900" data-testid="line-items-total">
                      £{((formData.line_items || []).reduce(
                        (sum, li) => sum + (parseFloat(li.rate) || 0) * (parseFloat(li.quantity) || 1), 0
                      )).toFixed(2)}
                    </span>
                  </div>
                </div>
              )}
            </div>

            {/* Address with Map Picker */}
            <div>
              <label className="flex items-center gap-2 text-sm font-medium text-zinc-700 mb-2">
                <MapPin size={16} className="text-zinc-400" />
                Job Address
              </label>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setShowAddressPicker(true)}
                  className="flex-1 px-4 py-2.5 rounded-xl border border-zinc-300 text-left text-zinc-900 hover:bg-zinc-50 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 outline-none transition-colors"
                >
                  {formData.address ? (
                    <span className="line-clamp-1">{formData.address}</span>
                  ) : (
                    <span className="text-zinc-400">Click to set address...</span>
                  )}
                </button>
                {(formData.latitude || formData.address) && (
                  <button
                    type="button"
                    onClick={handleNavigate}
                    className="px-3 py-2.5 rounded-xl bg-blue-500 text-white hover:bg-blue-600 transition-colors"
                    title="Navigate"
                  >
                    <Navigation size={18} />
                  </button>
                )}
              </div>
              {formData.latitude && formData.longitude && (
                <p className="text-xs text-emerald-600 mt-1 flex items-center gap-1">
                  <MapPin size={12} />
                  Location pinned ({formData.latitude.toFixed(4)}, {formData.longitude.toFixed(4)})
                </p>
              )}
            </div>

            {/* Mini Map Preview if coordinates exist */}
            {formData.latitude && formData.longitude && (
              <MiniMapPreview
                latitude={formData.latitude}
                longitude={formData.longitude}
                address={formData.address}
                height="128px"
                showNavigate={false}
                onClick={() => setShowAddressPicker(true)}
              />
            )}

            {/* Scheduled Date */}
            <div>
              <label className="flex items-center gap-2 text-sm font-medium text-zinc-700 mb-2">
                <Calendar size={16} className="text-zinc-400" />
                Scheduled Date
              </label>
              <input
                type="date"
                value={formData.scheduled_date}
                onChange={(e) => setFormData({...formData, scheduled_date: e.target.value})}
                onClick={(e) => e.stopPropagation()}
                onFocus={(e) => e.stopPropagation()}
                onTouchStart={(e) => e.stopPropagation()}
                onTouchEnd={(e) => e.stopPropagation()}
                onMouseDown={(e) => e.stopPropagation()}
                className="w-full px-4 py-2.5 rounded-xl border border-zinc-300 text-zinc-900 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 outline-none appearance-none"
                style={{ WebkitAppearance: 'none', minHeight: '48px' }}
                data-testid="job-scheduled-date"
              />
            </div>

            {/* Frequency */}
            <div>
              <label className="flex items-center gap-2 text-sm font-medium text-zinc-700 mb-2">
                <RefreshCw size={16} className="text-zinc-400" />
                Frequency
              </label>
              <select
                value={
                  formData.frequency === '' ? '' :
                  frequencyOptions.some(opt => opt.value === formData.frequency) ? formData.frequency :
                  showCustomFrequency ? 'custom' : 'custom'
                }
                onChange={(e) => {
                  if (e.target.value === 'custom') {
                    setShowCustomFrequency(true);
                    setCustomFrequencyValue(formData.frequency || '');
                  } else {
                    setShowCustomFrequency(false);
                    setFormData({...formData, frequency: e.target.value});
                  }
                }}
                className="w-full px-4 py-2.5 rounded-xl border border-zinc-300 text-zinc-900 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 outline-none"
              >
                {frequencyOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
                <option value="">One-off</option>
                <option value="custom">
                  {formData.frequency && !frequencyOptions.some(opt => opt.value === formData.frequency) && formData.frequency !== ''
                    ? `${formData.frequency} Weekly (Custom)`
                    : 'Enter your own...'}
                </option>
              </select>
              {showCustomFrequency && (
                <div className="mt-2 flex gap-2">
                  <input
                    type="number"
                    min="1"
                    max="52"
                    placeholder="Weeks"
                    value={customFrequencyValue}
                    onChange={(e) => setCustomFrequencyValue(e.target.value)}
                    className="flex-1 px-4 py-2 rounded-xl border border-zinc-300 text-zinc-900 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 outline-none"
                  />
                  <button
                    type="button"
                    onClick={() => {
                      if (customFrequencyValue && parseInt(customFrequencyValue) > 0) {
                        setFormData({...formData, frequency: customFrequencyValue});
                        setShowCustomFrequency(false);
                      }
                    }}
                    className="px-4 py-2 bg-emerald-500 text-white rounded-xl hover:bg-emerald-600"
                  >
                    Set
                  </button>
                </div>
              )}
              {formData.frequency === '' && !showCustomFrequency && (
                <p className="text-xs text-amber-600 mt-1">One-off job - will only appear on scheduled date</p>
              )}
            </div>

            {/* Assignee */}
            <div>
              <label className="flex items-center gap-2 text-sm font-medium text-zinc-700 mb-2">
                <Users size={16} className="text-zinc-400" />
                Assignee
              </label>
              <select
                value={formData.assignee}
                onChange={(e) => setFormData({...formData, assignee: e.target.value})}
                className="w-full px-4 py-2.5 rounded-xl border border-zinc-300 text-zinc-900 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 outline-none"
              >
                <option value="">Unassigned</option>
                {teamMembers.map(member => (
                  <option key={member.id} value={member.name}>
                    {member.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Round */}
            <div>
              <label className="flex items-center gap-2 text-sm font-medium text-zinc-700 mb-2">
                <Route size={16} className="text-zinc-400" />
                Round
              </label>
              <select
                value={formData.round}
                onChange={(e) => setFormData({...formData, round: e.target.value})}
                className="w-full px-4 py-2.5 rounded-xl border border-zinc-300 text-zinc-900 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 outline-none"
              >
                <option value="">No Round</option>
                {rounds.map(round => (
                  <option key={round.id} value={round.name}>
                    {round.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Notes (internal — workers see these on the job card) */}
            <div>
              <label className="flex items-center gap-2 text-sm font-medium text-zinc-700 mb-2">
                <FileText size={16} className="text-zinc-400" />
                Notes
              </label>
              <textarea
                value={formData.notes}
                onChange={(e) => setFormData({...formData, notes: e.target.value})}
                rows={3}
                className="w-full px-4 py-2.5 rounded-xl border border-zinc-300 text-zinc-900 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 outline-none resize-none"
                placeholder="Add notes about this job..."
              />
            </div>

            {/* Notes for invoice — customer-facing. Appears on the invoice
                PDF, email and public invoice page. Kept separate from the
                internal Notes above so workers can scribble reminders
                ("front-window scaffold needed") without leaking them to
                the customer. */}
            <div>
              <label className="flex items-center gap-2 text-sm font-medium text-zinc-700 mb-2">
                <FileText size={16} className="text-amber-500" />
                Notes for invoice
                <span className="text-[10px] uppercase tracking-wider text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded">Customer-facing</span>
              </label>
              <textarea
                value={formData.invoice_notes}
                onChange={(e) => setFormData({ ...formData, invoice_notes: e.target.value })}
                rows={2}
                className="w-full px-4 py-2.5 rounded-xl border border-zinc-300 text-zinc-900 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 outline-none resize-none"
                placeholder="e.g. Cleaned inside conservatory glass · Next visit due 6 weeks"
                data-testid="job-invoice-notes-input"
              />
              <p className="text-[11px] text-zinc-400 mt-1">Will appear on the invoice the customer receives.</p>
            </div>
          </div>

          {/* Footer Actions */}
          <div className="p-4 border-t border-zinc-200 space-y-3 flex-shrink-0">
            <div className="flex gap-3">
              <button
                onClick={onClose}
                className="flex-1 px-4 py-2.5 rounded-xl border border-zinc-300 text-zinc-700 font-medium hover:bg-zinc-50 transition-colors"
                disabled={isSaving}
              >
                Cancel
              </button>
              <button
                onClick={handleSaveClick}
                className="flex-1 px-4 py-2.5 rounded-xl bg-emerald-600 text-white font-medium hover:bg-emerald-700 transition-colors disabled:opacity-50"
                disabled={isSaving}
              >
                {isSaving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
            
            {/* Delete Button */}
            <button
              onClick={handleDelete}
              className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl border border-red-200 text-red-600 font-medium hover:bg-red-50 transition-colors"
              disabled={isSaving}
            >
              <Trash2 size={16} />
              Delete Job
            </button>
          </div>
        </div>
      </div>

      {/* Price Change Warning Modal */}
      {showPriceWarning && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[10000] p-4">
          <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl overflow-hidden">
            {/* Header */}
            <div className="bg-amber-500 p-4 text-white">
              <div className="flex items-center gap-3">
                <PoundSterling size={24} />
                <h3 className="text-lg font-semibold">Price Changed</h3>
              </div>
            </div>
            
            {/* Content */}
            <div className="p-6">
              <p className="text-zinc-700 mb-2">
                You've changed the price from <span className="font-bold">£{parseFloat(originalPrice).toFixed(2)}</span> to <span className="font-bold">£{parseFloat(formData.price).toFixed(2)}</span>
              </p>
              <p className="text-zinc-600 text-sm mb-6">
                This is a recurring job. How would you like to apply this change?
              </p>
              
              {/* Options */}
              <div className="space-y-3">
                <button
                  onClick={() => handleSave(true)}
                  className="w-full px-4 py-4 rounded-xl bg-emerald-50 hover:bg-emerald-100 border-2 border-emerald-200 text-left transition-colors"
                >
                  <div className="font-semibold text-emerald-700">Every time</div>
                  <div className="text-sm text-emerald-600">Update price for all future jobs of this type</div>
                </button>
                
                <button
                  onClick={() => handleSave(false)}
                  className="w-full px-4 py-4 rounded-xl bg-zinc-50 hover:bg-zinc-100 border-2 border-zinc-200 text-left transition-colors"
                >
                  <div className="font-semibold text-zinc-700">Just this once</div>
                  <div className="text-sm text-zinc-600">Only update this specific job</div>
                </button>
              </div>
            </div>
            
            {/* Cancel */}
            <div className="p-4 border-t border-zinc-200">
              <button
                onClick={() => setShowPriceWarning(false)}
                className="w-full px-4 py-2.5 rounded-xl border border-zinc-300 text-zinc-700 font-medium hover:bg-zinc-50 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Address Picker Modal */}
      <AddressPickerModal
        isOpen={showAddressPicker}
        onClose={() => setShowAddressPicker(false)}
        onSave={handleAddressSave}
        initialAddress={formData.address}
        initialLat={formData.latitude}
        initialLng={formData.longitude}
      />
    </>
  );
};

export default JobEditModal;
